import { useMemo, useState } from "react";

import { Column, Row, Text, ToggleChevron, UsdPriceTextWithTooltip } from "@bridgesplit/ui";
import { COPY } from "app/constants";
import { PortfolioTab } from "app/utils";
import { Collapse, Divider, Skeleton } from "@mui/material";

import { PortfolioAggregates } from "./stats/usePortfolioAggregates";
import EarnSection from "./EarnSection";
import LendVaultSection from "./LendVaultSection";
import LoopsSection from "./LoopsSection";
import LoansSection from "./LoansSection";
import StrategiesSection from "./StrategiesSection";

interface SectionContentProps {
    isExpanded: boolean;
    isUsdValueLoading: boolean; // we do this because we dont want to expand the section if there is no usdValue, but we also want to render the components so their loading state isnt blocked by the top level stats query
}

interface PortfolioSection {
    label: string;
    Component: React.ComponentType<SectionContentProps>;
    hide?: boolean;
    id: PortfolioTab;
    usdValue?: number;
}

export default function PortfolioSections({ aggregates }: { aggregates: PortfolioAggregates }) {
    const { sections, isLoading } = useSections(aggregates);

    return (
        <>
            {sections.map((section) => (
                <Section key={section.id} section={section} isUsdValueLoading={isLoading} />
            ))}
        </>
    );
}

function Section({ section, isUsdValueLoading }: { section: PortfolioSection; isUsdValueLoading: boolean }) {
    const [expandedState, setIsExpanded] = useState<boolean | undefined>();
    const isExpanded = expandedState ?? !!section.usdValue;
    const { Component } = section;
    return (
        <Column spacing={2}>
            <Row sx={{ cursor: "pointer" }} spaceBetween onClick={() => setIsExpanded(!isExpanded)}>
                <Row data-testid={`portfolio-section-label-${section.id}`}>
                    <Text loading={isUsdValueLoading} variant="h4">
                        {section.label}
                    </Text>
                </Row>
                {!isUsdValueLoading && (
                    <Row>
                        {section.usdValue !== undefined && (
                            <Row data-testid={`portfolio-section-value-${section.id}`}>
                                <UsdPriceTextWithTooltip
                                    textColor="body"
                                    rawUsdValue={section.usdValue}
                                    loading={isUsdValueLoading}
                                />
                            </Row>
                        )}
                        <ToggleChevron textVariant="h3" expanded={isExpanded} />
                    </Row>
                )}
            </Row>

            <Component isExpanded={isExpanded} isUsdValueLoading={isUsdValueLoading} />
        </Column>
    );
}

function useSections(aggregates: PortfolioAggregates) {
    const { borrowAggregate, earnTotalValueUsd, loopsAggregate, strategiesAggregate, vaultsAggregate, isLoading } =
        aggregates;

    const sections = useMemo((): PortfolioSection[] => {
        const borrowNetValue = borrowAggregate
            ? Math.max(
                  borrowAggregate.collateralUsd - borrowAggregate.principalUsd - borrowAggregate.interestAccruedUsd,
                  0
              )
            : undefined;
        const loopsNetValue = loopsAggregate
            ? Math.max(
                  loopsAggregate.collateralUsd - loopsAggregate.principalUsd - loopsAggregate.interestAccruedUsd,
                  0
              )
            : undefined;

        return [
            {
                label: COPY.EARN_TERM,
                Component: EarnSection,
                id: PortfolioTab.Earn,
                usdValue: earnTotalValueUsd
            },
            {
                label: COPY.LEND_VAULTS_TERM_PLURAL,
                Component: LendVaultSection,
                id: PortfolioTab.LendVaults,
                usdValue: vaultsAggregate?.totalValueUsd
            },
            {
                label: COPY.LOOP_TERM_PLURAL,
                Component: LoopsSection,
                id: PortfolioTab.Vaults,
                usdValue: loopsNetValue
            },
            {
                label: COPY.BORROW_TERM_GERUND,
                Component: LoansSection,
                id: PortfolioTab.Loans,
                usdValue: borrowNetValue
            },
            {
                label: COPY.STRATEGY_TERM_PLURAL,
                Component: StrategiesSection,
                id: PortfolioTab.Strategies,
                usdValue: strategiesAggregate?.totalSupplyUsd
            }
        ];
    }, [borrowAggregate, earnTotalValueUsd, loopsAggregate, strategiesAggregate, vaultsAggregate]);

    return { sections, isLoading };
}

export function SectionSkeleton() {
    return (
        <>
            <Divider />
            <Skeleton sx={{ width: "100%", height: 200 }} />
        </>
    );
}

export function SectionCollapse({ isExpanded, children }: { isExpanded: boolean; children: React.ReactNode }) {
    return (
        <Collapse in={isExpanded}>
            <Column sx={{ mb: 4 }} spacing={2}>
                <Divider />
                {children}
            </Column>
        </Collapse>
    );
}
